import { useState } from 'react';
import {
  ListChecks,
  CheckSquare,
  RefreshCw,
  Headphones,
  Handshake,
  Clock,
  AlertTriangle,
  Bell,
  X,
  AlarmClock,
} from 'lucide-react';
import {
  useActivityQueue,
  useDismissQueueItem,
  type QueueItem,
  type QueueItemType,
} from '../hooks/useActivityQueue';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface ActivityQueueViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const typeIcons: Record<QueueItemType, React.ComponentType<{ className?: string }>> = {
  task: CheckSquare,
  follow_up: Bell,
  renewal: RefreshCw,
  case: Headphones,
  deal: Handshake,
  approval: AlertTriangle,
};

const priorityColors: Record<string, string> = {
  low: 'gray',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
};

function groupItems(items: QueueItem[]) {
  const overdue: QueueItem[] = [];
  const dueToday: QueueItem[] = [];
  const cases: QueueItem[] = [];
  const followUps: QueueItem[] = [];

  for (const item of items) {
    if (item.isOverdue) {
      overdue.push(item);
    } else if (item.isDueToday) {
      dueToday.push(item);
    } else if (item.type === 'case') {
      cases.push(item);
    } else {
      followUps.push(item);
    }
  }

  return { overdue, dueToday, cases, followUps };
}

interface QueueSectionProps {
  title: string;
  items: QueueItem[];
  color: string;
  onDismiss: (id: string) => void;
  onSnooze: (id: string) => void;
}

function QueueSection({ title, items, color, onDismiss, onSnooze }: QueueSectionProps) {
  if (items.length === 0) return null;

  const borderColor: Record<string, string> = {
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
    blue: 'border-l-blue-500',
    gray: 'border-l-slate-300',
  };

  const titleColor: Record<string, string> = {
    red: 'text-red-700',
    amber: 'text-amber-700',
    blue: 'text-blue-700',
    gray: 'text-slate-600',
  };

  return (
    <div className="mb-6">
      <h3 className={cn('mb-2 text-sm font-semibold', titleColor[color] ?? 'text-slate-700')}>
        {title} ({items.length})
      </h3>
      <div className="card divide-y divide-surface-100">
        {items.map((item) => {
          const Icon = typeIcons[item.type] ?? Bell;
          return (
            <div
              key={item.id}
              className={cn(
                'flex items-center gap-4 border-l-4 px-4 py-3',
                borderColor[color] ?? 'border-l-slate-300',
              )}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-100">
                <Icon className="h-4 w-4 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 truncate">{item.title}</span>
                  <Badge color={priorityColors[item.priority]} size="sm">{item.priority}</Badge>
                </div>
                <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
                  {item.entityName && <span>{item.entityName}</span>}
                  {item.dueDate && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(item.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onSnooze(item.id)}
                  className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                  title="Snooze 1 hour"
                >
                  <AlarmClock className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDismiss(item.id)}
                  className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                  title="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ActivityQueueView({ onNavigate }: ActivityQueueViewProps) {
  const { data, isLoading, error } = useActivityQueue();
  const dismissItem = useDismissQueueItem();

  const items = data?.items ?? [];
  const { overdue, dueToday, cases, followUps } = groupItems(items);

  function handleDismiss(id: string) {
    dismissItem.mutate({ id });
  }

  function handleSnooze(id: string) {
    dismissItem.mutate({ id, snoozeMinutes: 60 });
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">My Queue</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data
              ? `${data.total} item${data.total !== 1 ? 's' : ''} \u00b7 ${data.overdueCount} overdue`
              : 'Prioritized work queue'}
          </p>
        </div>
      </div>

      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load queue. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-8 w-8 animate-pulse rounded-lg bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1.5 h-3 w-32 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length > 0 ? (
          <>
            <QueueSection title="Overdue" items={overdue} color="red" onDismiss={handleDismiss} onSnooze={handleSnooze} />
            <QueueSection title="Due Today" items={dueToday} color="amber" onDismiss={handleDismiss} onSnooze={handleSnooze} />
            <QueueSection title="Cases" items={cases} color="blue" onDismiss={handleDismiss} onSnooze={handleSnooze} />
            <QueueSection title="Follow-ups" items={followUps} color="gray" onDismiss={handleDismiss} onSnooze={handleSnooze} />
          </>
        ) : (
          <EmptyState
            icon={ListChecks}
            title="You're all caught up!"
            description="No pending items in your queue. Great job staying on top of things."
          />
        )}
      </div>
    </div>
  );
}
